import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, Search, Building2, Pencil, Trash2, MapPin, Phone, Mail, Hash, KeyRound, X, Merge } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { AccesPortailContacts } from '../components/entreprise/AccesPortailContacts';
import { ROLES, ROLE_COLORS, RolesSelector, IdentiteFields, ContactsEditor, FinancementFields, emptySociete, fromSociete, toPayload } from '../components/societe/SocieteFormParts';
import { TYPES_FINANCEUR } from '../lib/financeurConstants';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RoleBadges = ({ roles, financement }) => (
  <div className="flex flex-wrap gap-1">
    {ROLES.filter(r => roles?.[r.key]).map(r => (
      <span key={r.key} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${ROLE_COLORS[r.color]}`} data-testid={`badge-role-${r.key}`}>
        {r.label}{r.key === 'financeur' && financement?.type_financeur ? ` · ${TYPES_FINANCEUR.find(t => t.value === financement.type_financeur)?.label || financement.type_financeur}` : ''}
      </span>
    ))}
  </div>
);

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2 p-2 bg-slate-50 rounded-lg"><Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5" /><div><p className="text-[10px] uppercase text-slate-400">{label}</p><p className="text-sm text-slate-800">{value || '—'}</p></div></div>
);

export default function SocietesPage() {
  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('tous');
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptySociete());
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fusionOpen, setFusionOpen] = useState(false);
  const [fusionCible, setFusionCible] = useState('');

  const load = async () => {
    try { const r = await axios.get(`${API_URL}/societes`); setSocietes(r.data); } catch { toast.error('Erreur lors du chargement des sociétés'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const openDetail = async (s) => { try { const r = await axios.get(`${API_URL}/societes/${s.id}`); setSelected(r.data); } catch { setSelected(s); } };
  const openForm = (s = null) => { setEditing(s); setForm(s ? fromSociete(s) : emptySociete()); setDialogOpen(true); };
  const closeForm = () => { setDialogOpen(false); setEditing(null); setForm(emptySociete()); };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toPayload(form);
      const r = editing ? await axios.put(`${API_URL}/societes/${editing.id}`, payload) : await axios.post(`${API_URL}/societes`, payload);
      toast.success(editing ? 'Société mise à jour' : 'Société créée');
      closeForm(); await load();
      if (selected?.id === r.data.id || !editing) openDetail(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde'); } finally { setSaving(false); }
  };
  // Fusion : la société sélectionnée (source) disparaît, toutes ses références basculent sur la cible
  const fusionner = async () => {
    if (!selected || !fusionCible) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API_URL}/societes/${selected.id}/fusionner`, { cible_id: fusionCible });
      const st = r.data.stats || {};
      toast.success(`Fusion effectuée dans ${r.data.cible?.nom} — ${st.actions_client || 0} action(s), ${st.df_financeur || 0} DF financeur, ${st.df_entreprise_finale || 0} entreprise(s) finale(s), ${st.candidats || 0} candidat(s) re-pointés`);
      setFusionOpen(false); setFusionCible('');
      await load(); openDetail(r.data.cible);
    } catch (err) { toast.error(err.response?.data?.detail || 'Fusion impossible'); } finally { setSaving(false); }
  };
  const remove = async () => {
    try { await axios.delete(`${API_URL}/societes/${deleteId}`); toast.success('Société supprimée'); if (selected?.id === deleteId) setSelected(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Suppression impossible'); } finally { setDeleteId(null); }
  };

  const filtered = societes.filter(s => (roleFilter === 'tous' || s.roles?.[roleFilter]) &&
    (!search || [s.nom, s.ville, s.siret, s.code].some(v => (v || '').toLowerCase().includes(search.toLowerCase()))));
  const counts = { tous: societes.length, ...Object.fromEntries(ROLES.map(r => [r.key, societes.filter(s => s.roles?.[r.key]).length])) };
  const isClientLike = form.roles.client || form.roles.entreprise_finale;

  return (
    <DashboardLayout>
      <div className="p-6 space-y-5" data-testid="societes-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Building2 className="w-6 h-6 text-amber-600" /> Sociétés</h1>
            <p className="text-sm text-slate-500">Une seule fiche par société : client, financeur et/ou entreprise finale.</p>
          </div>
          <Button onClick={() => openForm()} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-nouvelle-societe"><Plus className="w-4 h-4 mr-2" />Nouvelle société</Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px] max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher (nom, ville, SIRET)…" className="pl-9" data-testid="search-societes" /></div>
          {[{ key: 'tous', label: 'Toutes' }, ...ROLES].map(r => (
            <button type="button" key={r.key} onClick={() => setRoleFilter(r.key)} data-testid={`filter-role-${r.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${roleFilter === r.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{r.label} ({counts[r.key]})</button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <Card className={`border-0 shadow-sm ${selected ? 'lg:col-span-3' : 'lg:col-span-5'}`}>
            <CardContent className="p-0">
              {loading ? <p className="p-6 text-sm text-slate-400">Chargement…</p> : (
                <div className="divide-y">
                  {filtered.map(s => (
                    <div key={s.id} onClick={() => openDetail(s)} className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 ${selected?.id === s.id ? 'bg-amber-50/60' : ''}`} data-testid={`societe-row-${s.id}`}>
                      <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm shrink-0">{(s.nom || '?')[0].toUpperCase()}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{s.code && <span className="font-mono text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded mr-1.5" data-testid={`societe-code-${s.id}`}>{s.code}</span>}{s.nom}</p>
                        <p className="text-xs text-slate-500 truncate">{[s.ville, s.siret].filter(Boolean).join(' · ') || 'Aucune coordonnée'}{s.contacts?.length ? ` · ${s.contacts.length} contact(s)` : ''}</p>
                      </div>
                      <RoleBadges roles={s.roles} financement={s.financement} />
                      <div className="flex gap-1 shrink-0">
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); openForm(s); }} data-testid={`edit-societe-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={(e) => { e.stopPropagation(); setDeleteId(s.id); }} data-testid={`delete-societe-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  ))}
                  {filtered.length === 0 && <p className="p-8 text-sm text-slate-400 text-center italic">Aucune société</p>}
                </div>
              )}
            </CardContent>
          </Card>

          {selected && (
            <Card className="border-0 shadow-sm lg:col-span-2" data-testid="societe-detail">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-start justify-between gap-2">
                  <div><h2 className="text-lg font-bold text-slate-900">{selected.code && <span className="font-mono text-xs text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded mr-2 align-middle">{selected.code}</span>}{selected.nom}</h2><RoleBadges roles={selected.roles} financement={selected.financement} /></div>
                  <div className="flex gap-1"><Button type="button" size="sm" variant="outline" onClick={() => openForm(selected)} data-testid="btn-edit-selected"><Pencil className="w-3.5 h-3.5 mr-1" />Modifier</Button><Button type="button" size="sm" variant="outline" className="text-violet-700 border-violet-200 hover:bg-violet-50" onClick={() => { setFusionCible(''); setFusionOpen(true); }} data-testid="btn-fusionner-selected" title="Fusionner cette société (doublon) dans une autre"><Merge className="w-3.5 h-3.5 mr-1" />Fusionner</Button><Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => setSelected(null)}><X className="w-4 h-4" /></Button></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Info icon={MapPin} label="Adresse" value={[selected.adresse, selected.code_postal, selected.ville].filter(Boolean).join(', ')} />
                  <Info icon={Hash} label="SIRET / TVA" value={[selected.siret, selected.tva_intracom].filter(Boolean).join(' · ')} />
                  <Info icon={Phone} label="Téléphone" value={selected.telephone} />
                  <Info icon={Mail} label="Email" value={selected.email} />
                  <Info icon={Mail} label="Email facturation électronique" value={selected.email_facturation} />
                </div>
                {selected.usage && (
                  <div className="text-xs text-slate-600 bg-slate-50 rounded-lg p-2.5 grid grid-cols-2 gap-1" data-testid="societe-usage">
                    <span>Actions (client) : <b>{selected.usage.actions_client}</b></span><span>Dossiers financeurs : <b>{selected.usage.df_financeur}</b></span>
                    <span>Entreprise finale : <b>{selected.usage.df_entreprise_finale}</b></span><span>Accès portail : <b>{selected.usage.acces_portail}</b></span>
                  </div>
                )}
                {selected.contacts?.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-slate-500 uppercase">Contacts ({selected.contacts.length})</p>
                    {selected.contacts.map((c, i) => (
                      <div key={c.id || i} className="text-sm bg-slate-50 rounded-lg px-3 py-2">
                        <p className="font-medium text-slate-800">{c.civilite} {c.prenom} {c.nom} <span className="text-[10px] text-slate-400 ml-1">{c.type_contact}</span></p>
                        <p className="text-xs text-slate-500">{[c.fonction, c.email, c.telephone].filter(Boolean).join(' · ')}</p>
                      </div>
                    ))}
                  </div>
                )}
                {(selected.roles?.client || selected.roles?.entreprise_finale || selected.roles?.financeur) && (
                  <div className="border-t pt-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Accès aux portails</p>
                    <AccesPortailContacts client={{ id: selected.client_id, nom: selected.nom, contacts: selected.contacts || [] }} portails={[...((selected.roles?.client || selected.roles?.entreprise_finale) ? ['entreprise'] : []), ...(selected.roles?.financeur ? ['financeur'] : [])]} />
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <Dialog open={fusionOpen} onOpenChange={setFusionOpen}>
          <DialogContent className="max-w-lg" data-testid="fusion-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Merge className="w-5 h-5 text-violet-600" /> Fusionner « {selected?.nom} » dans…</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">Cette société (doublon) sera <b>supprimée</b> et toutes ses références (actions, dossiers financeurs, entreprise finale, candidats, accès portail, bons de commande) basculeront sur la société cible, qui gardera son nom et son code.</p>
            <Select value={fusionCible} onValueChange={setFusionCible}>
              <SelectTrigger data-testid="fusion-cible-select"><SelectValue placeholder="Choisir la société cible (conservée)…" /></SelectTrigger>
              <SelectContent>
                {societes.filter(s => s.id !== selected?.id).map(s => <SelectItem key={s.id} value={s.id} data-testid={`fusion-cible-${s.id}`}><span className="font-mono text-[10px] text-amber-700 mr-1">{s.code}</span>{s.nom}{s.ville ? ` · ${s.ville}` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFusionOpen(false)}>Annuler</Button>
              <Button type="button" className="bg-violet-600 hover:bg-violet-700" disabled={!fusionCible || saving} onClick={fusionner} data-testid="confirm-fusion">{saving ? 'Fusion…' : 'Fusionner'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeForm(); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="societe-dialog">
            <DialogHeader><DialogTitle>{editing ? `Modifier — ${editing.nom}` : 'Nouvelle société'}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <RolesSelector roles={form.roles} onChange={(r) => set('roles', r)} />
              <Tabs defaultValue="identite">
                <TabsList>
                  <TabsTrigger value="identite" data-testid="tab-societe-identite">Identité</TabsTrigger>
                  <TabsTrigger value="contacts" data-testid="tab-societe-contacts">Contacts ({form.contacts.length})</TabsTrigger>
                  <TabsTrigger value="financement" disabled={!form.roles.financeur} className={form.roles.financeur ? '' : 'hidden'} data-testid="tab-societe-financement">Financement</TabsTrigger>
                </TabsList>
                <TabsContent value="identite" className="pt-3"><IdentiteFields form={form} set={set} /></TabsContent>
                <TabsContent value="contacts" className="pt-3"><ContactsEditor contacts={form.contacts} onChange={(c) => set('contacts', c)} /></TabsContent>
                <TabsContent value="financement" className="pt-3">{form.roles.financeur && <FinancementFields fin={form.financement} onChange={(f) => set('financement', f)} />}</TabsContent>
              </Tabs>
              {!isClientLike && !form.roles.financeur && <p className="text-xs text-red-600">Sélectionnez au moins un rôle.</p>}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeForm}>Annuler</Button>
                <Button type="submit" disabled={saving || (!isClientLike && !form.roles.financeur)} className="bg-amber-600 hover:bg-amber-700" data-testid="submit-societe">{editing ? 'Mettre à jour' : 'Créer'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Supprimer cette société ?</AlertDialogTitle><AlertDialogDescription>La suppression est refusée si la société est utilisée dans une action, un dossier financeur, un candidat ou un accès portail.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-societe">Supprimer</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
